// src/services/authService.ts
import pb from "../api/pocketbase";
import Cookies from "js-cookie";
import { AuthData, User } from "../types/auth";
import { UsersRecord } from "../types/pocketbase-types";

const signUp = async (
    email: string,
    password: string,
    passwordConfirm: string,
    phone: string
): Promise<void> => {
    try {
        // Create the user (phone with country code is required at registration)
        await pb.collection("users").create({
            email,
            password,
            passwordConfirm,
            phone,
            emailVisibility: true,
        });
        
        // Request email verification
        await pb.collection("users").requestVerification(email);
    } catch (error: any) {
        if (error?.response?.data) {
            const errData = error.response.data;
            if (errData.email?.message) {
                throw new Error(errData.email.message);
            }
            if (errData.password?.message) {
                throw new Error(errData.password.message);
            }
        }
        throw error;
    }
};

const login = async (email: string, password: string): Promise<AuthData> => {
    try {
        const authData = (await pb
            .collection("users")
            .authWithPassword(email, password)) as AuthData;
        
        // Check if email is verified
        if (!authData.record.verified) {
            // Clear the auth store since user is not verified
            pb.authStore.clear();
            throw new Error("EMAIL_NOT_VERIFIED");
        }
        
        // Store the token in cookies
        Cookies.set("token", authData.token, { expires: 7 }); // 7 days expiry
        return authData;
    } catch (error: any) {
        if (error.message === "EMAIL_NOT_VERIFIED") {
            throw error;
        }
        throw new Error("Invalid email or password");
    }
};

const logout = (): void => {
    pb.authStore.clear();
    Cookies.remove("token");
};

const isAuthenticated = (): boolean => {
    return pb.authStore.isValid;
};

const getToken = (): string | null => {
    return pb.authStore.token || Cookies.get("token") || null;
};

const getUserId = (): string | null => {
    return pb.authStore.model?.id ?? null;
};

const getUser = (): User | null => {
    return (pb.authStore.model as unknown as User) ?? null;
};

const isAdmin = (): boolean => {
    return (pb.authStore.model as any)?.role === "admin";
};

// Refresh the cached auth record (picks up profile/role changes made elsewhere)
const refreshUser = async (): Promise<User | null> => {
    if (!pb.authStore.isValid) return null;
    const authData = await pb.collection("users").authRefresh();
    return (authData.record as unknown as User) ?? null;
};

// Update the logged-in user's profile fields, then re-sync the auth store.
const updateProfile = async (data: Partial<UsersRecord>): Promise<User | null> => {
    const id = getUserId();
    if (!id) throw new Error("Not authenticated");
    await pb.collection("users").update(id, data);
    return refreshUser();
};

// Upload/replace the logged-in user's avatar, then re-sync the auth store.
const updateAvatar = async (file: File): Promise<User | null> => {
    const id = getUserId();
    if (!id) throw new Error("Not authenticated");
    const form = new FormData();
    form.append("avatar", file);
    await pb.collection("users").update(id, form);
    return refreshUser();
};

const resetPassword = async (email: string) => {
    return pb.collection("users").requestPasswordReset(email);
};

const confirmNewPassword = async (
    token: string,
    newPassword: string,
    newPasswordConfirm: string
) => {
    return pb
        .collection("users")
        .confirmPasswordReset(token, newPassword, newPasswordConfirm);
};

const requestVerification = async (email: string) => {
    return pb.collection("users").requestVerification(email);
};

const confirmVerification = async (token: string) => {
    return pb.collection("users").confirmVerification(token);
};

export {
    login,
    logout,
    isAuthenticated,
    isAdmin,
    getToken,
    getUserId,
    getUser,
    refreshUser,
    updateProfile,
    updateAvatar,
    signUp,
    resetPassword,
    confirmNewPassword,
    requestVerification,
    confirmVerification,
};
